import {useEffect, useState} from 'react';

import {APIError} from 'data/errors';
import {IFormData} from 'presentation/pages/public/sign-up/components/Forms/Step4/schema';
import {formatApiErrorResponse} from 'presentation/utils/formatApiErrorResponse';
import {OperationStateType, OperationStateTypesEnum} from 'presentation/constants';
import {translations} from 'presentation/translations-new';
import {HttpStatusCode} from 'data/protocols/http';
import {useForgotPasswordData} from '../useForgotPasswordData';
import {forgotPasswordConfirm} from '../../services/forgotPasswordConfirm';
import {forgotPasswordConfirmLegacy} from '../../services/forgotPasswordConfirmLegacy';
import {UseForgotPasswordConfirmProps} from './types';
import {REUSED_PASSWORD_ERROR} from './constants';

const {serverError} = translations['pt-br'].commons;

export function useForgotPasswordConfirm({methods, privateFlow}: UseForgotPasswordConfirmProps) {
	const {
		getValues,
		formState: {isValidating},
	} = methods;

	const [requestState, setRequestState] = useState<OperationStateType | null>(null);
	const [confirmationModalState, setConfirmationModalState] = useState(false);
	const [errorModalState, setErrorModalState] = useState(false);
	const [apiErrorMsg, setApiErrorMsg] = useState('');
	const {dtNascimento, cdEmpresaPlano, id, tipoId, token, cdPessoa} =
		useForgotPasswordData().data;
	const formFieldValues = getValues();

	const isDirty = Object.values(formFieldValues).every(val => val !== '');
	const showAPIError = requestState === OperationStateTypesEnum.ERROR && isDirty;
	const showConfirmationModal = requestState === OperationStateTypesEnum.DONE;

	const handleSubmitRequest = async ({password, repeatPassword}: IFormData) => {
		try {
			if (!privateFlow) {
				const payload = {
					cdEmpresaPlano,
					cdSenha: password,
					contraSenha: repeatPassword,
					dtNascimento,
					id,
					tipoId,
					token,
				};
				setRequestState(OperationStateTypesEnum.PENDING);
				await forgotPasswordConfirm(payload);
			} else {
				await forgotPasswordConfirmLegacy({
					cdPessoa: String(cdPessoa),
					cdSenha: password,
					contraSenha: repeatPassword,
					token,
				});
			}

			setRequestState(OperationStateTypesEnum.DONE);
			setConfirmationModalState(true);
		} catch (error) {
			if (error instanceof APIError) {
				setRequestState(OperationStateTypesEnum.ERROR);

				const parsedResponse = formatApiErrorResponse(error);
				setApiErrorMsg(parsedResponse);

				if (error.statusCode === HttpStatusCode.serverError) {
					setApiErrorMsg(serverError);
				}

				if (parsedResponse !== REUSED_PASSWORD_ERROR) {
					setErrorModalState(true);
				}
			}
		}
	};

	const clearAPIError = () => {
		setRequestState(null);
	};

	useEffect(() => {
		if (isValidating) {
			clearAPIError();
		}
	}, [isValidating]);

	return {
		handleSubmitRequest,
		apiErrorMsg,
		showAPIError,
		showConfirmationModal,
		confirmationModalState,
		setConfirmationModalState,
		errorModalState,
		setErrorModalState,
	};
}
